import Button from '@atlaskit/button';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import React, { useContext } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { fieldEditor_showEditChildOptionButton, getLastOptionIdOrNull } from '../../util/ui-helper';
import { GlobalContext } from '../../common/GlobalContext';
import { AuthenticatedUserCapabilities, CustomFieldType, IdAndValue } from '../../../model/model';
import { FieldEditorActionReturnType, OptionValidationErrorForView } from '../../stateful/field-editor-stateful/FieldEditorStateful';
import { FieldEditorOptionAction, FieldEditorOptionActionDelete, FieldEditorOptionActionOpenChildren, FieldEditorOptionActionUpdate, OptionListColumnDimensions } from './FieldEditor';
import { FieldEditorDeleteOptionWithConfirm } from './FieldEditorDeleteWithConfirm';
import { FieldEditorInPlaceEditOptionValue } from './FieldEditorInPlaceEditOptionValue';
import { FieldEditorSetDefaultOptionButton } from './FieldEditorSetDefaultOptionButton';
import { OptionSearchMatchMarker } from './search/custom-field-options-search-logic';
import { DTOCustomFieldOption, DTOGetDefaultOption } from 'generated-common';
import { token } from '@atlaskit/tokens';

export const FieldEditorViewAndEditOptionRow = ({
  option,
  defaultOption,
  fieldType,
  parentOptionIds,
  onUpdateOption,
  validationErrorFromServer,
  authenticatedUserCapabilities,
  allOptionIdsAndValuesLowerCase,
  searchMatch,
  columnDimensions,
  isDeleting,
  isUpdatingOptionIdOrNull,
  isLoading,
  isDragging,
  provided,
  isClone,
  isGroupedOver,
  style,
  index,
}: {
  option: DTOCustomFieldOption;
  defaultOption: DTOGetDefaultOption;
  fieldType: CustomFieldType;
  parentOptionIds: null | string[];
  onUpdateOption: (action: FieldEditorOptionAction) => Promise<FieldEditorActionReturnType>;
  validationErrorFromServer: OptionValidationErrorForView | null;
  authenticatedUserCapabilities: AuthenticatedUserCapabilities;
  allOptionIdsAndValuesLowerCase: IdAndValue[];
  searchMatch: OptionSearchMatchMarker;
  columnDimensions: OptionListColumnDimensions;
  isDeleting: string | null;
  isUpdatingOptionIdOrNull: string | null;
  isLoading: boolean;
  isDragging: boolean;
  provided: DraggableProvided;
  isClone?: boolean;
  isGroupedOver?: boolean;
  style?: any;
  index?: number;
}) => {
  const { t } = useContext(GlobalContext);

  function getStyle(provided: DraggableProvided, style: any) {
    if (!style) {
      return provided.draggableProps.style;
    }

    return {
      ...provided.draggableProps.style,
      ...style,
      ...{ backgroundColor: isDragging ? token('elevation.surface.overlay.hovered', '#f4f5f7') : token('elevation.surface', '#fff') },
    };
  }

  return (
    <div data-e2e-field-editor-row={option.id} key={`oss-${option.id}`} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={getStyle(provided, style)}>
      <div style={{ display: 'flex' }}>
        <div style={{ width: columnDimensions.positionPx, maxWidth: columnDimensions.positionPx, minWidth: columnDimensions.positionPx }}>
          <div style={{ paddingLeft: columnDimensions.positionPaddingLeftPx, marginTop: 16, fontSize: 10, color: token('color.text.subtlest', '#ccc') }}>{index !== undefined ? index + 1 : ''}</div>
        </div>
        <div style={{ width: columnDimensions.activePx, maxWidth: columnDimensions.activePx, minWidth: columnDimensions.activePx, paddingTop: 9 }}>
          <div data-e2e-edit-inline-disabled={option.id} data-e2e-edit-inline-disabled-value={!option.disabled}>
            <Toggle
              isDisabled={isDeleting === option.id || isUpdatingOptionIdOrNull === option.id}
              isChecked={!option.disabled}
              onChange={() => onUpdateOption(new FieldEditorOptionActionUpdate(Object.assign({}, option, { disabled: !option.disabled })))}
            />
            {/* WARN WARN: Never show the sequence. IT IS CURRENTLY NOT CORRECTLY RECALCULATED! option.sequence*/}
          </div>
        </div>
        <div style={{ flex: 2, minWidth: 0 /* VERY IMPORTANT FOR TEXT-OVERFLOW - SEE https://css-tricks.com/flexbox-truncated-text/ */ }}>
          <FieldEditorInPlaceEditOptionValue
            searchMatch={searchMatch}
            isDisabled={isDeleting === option.id || isUpdatingOptionIdOrNull === option.id}
            option={option}
            validationErrorFromServer={validationErrorFromServer}
            onUpdatedOption={(updated) => onUpdateOption(new FieldEditorOptionActionUpdate(updated))}
            allOptionIdsAndValuesLowerCase={allOptionIdsAndValuesLowerCase}
          />
        </div>

        <div style={{ width: columnDimensions.defaultPx, maxWidth: columnDimensions.defaultPx, minWidth: columnDimensions.defaultPx, paddingTop: 6 }}>
          <FieldEditorSetDefaultOptionButton
            parentOptionIds={parentOptionIds}
            option={option}
            isDisabled={isDeleting === option.id || isUpdatingOptionIdOrNull === option.id || isLoading === true}
            defaultOption={defaultOption}
            fieldType={fieldType}
            onUpdateOption={onUpdateOption}
          />
        </div>

        {fieldEditor_showEditChildOptionButton(getLastOptionIdOrNull(parentOptionIds), option.id, fieldType) ? (
          <div style={{ width: columnDimensions.childPx, maxWidth: columnDimensions.childPx, minWidth: columnDimensions.childPx, paddingTop: 6 }}>
            <Tooltip content={t('fieldEditor.editChildOptions')}>
              <span data-e2e-edit-child-options={option.id}>
                <Button
                  appearance="subtle"
                  isDisabled={isDeleting === option.id || isUpdatingOptionIdOrNull === option.id}
                  iconBefore={<ChildIssuesIcon label="child options" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
                  onClick={() => onUpdateOption(new FieldEditorOptionActionOpenChildren(option))}
                />
              </span>
            </Tooltip>
          </div>
        ) : null}
        <div style={{ width: columnDimensions.deletePx, maxWidth: columnDimensions.deletePx, minWidth: columnDimensions.deletePx, paddingTop: 6 }}>
          <FieldEditorDeleteOptionWithConfirm
            option={option}
            isDisabled={isDeleting != null || isUpdatingOptionIdOrNull !== null}
            isDisabledDueToPermission={!authenticatedUserCapabilities.userAllowedToDeleteOptions}
            onDeleted={(optionToDelete: DTOCustomFieldOption) => {
              onUpdateOption(new FieldEditorOptionActionDelete(optionToDelete)).then((error) => {
                // do nothing
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
